import React, { useEffect, useRef, useState } from 'react';
import ContentEditable from '../../utils/SafeContentEditable';
import { flattenBlocks } from '../../utils/blockUtils';
import { focusEditable } from '../../utils/focusUtils';
import {
  htmlToText,
  isCaretAtEnd,
  isCaretAtStart,
  isCaretOnFirstLine,
  isCaretOnLastLine,
} from '../../utils/caretUtils';
import { EDITABLE_TYPES } from '../../utils/editorConstants';
import { indentStyle } from '../../utils/indentUtils';
import {
  activeChoicePathId,
  choicePaths,
  choiceTabColor,
  createChoicePath,
  nextChoiceLabel,
} from '../../utils/choiceUtils';
import { CalloutMenu } from '../CalloutBlock/components/CalloutMenu';
import { useCalloutMenu } from '../CalloutBlock/hooks/useCalloutMenu';
import {
  CALLOUT_PALETTE,
  calloutSurfaceStyle,
  colorToRgb,
  resolveCalloutSurface,
} from '../CalloutBlock/utils/calloutTheme';
import { ChoicePathBlock } from '../ChoicePathBlock';

/**
 * Choice — a section box with tabs. Each tab is a `choice-path` of normal
 * blocks. Pick one path, or All (also forced when printing). Fill / border
 * / none use the same surface props as a callout (right-click or handle).
 */
export const ChoiceBlock = ({ block, onUpdate, onNavigate, ...passThrough }) => {
  const paths = choicePaths(block);
  const showAll = !!block.props?.showAll;
  const activeId = activeChoicePathId(block);
  const blockRef = useRef(block);
  blockRef.current = block;
  const color = colorToRgb(block.props?.color) ? block.props.color : 'neutral';
  const rgb = colorToRgb(color) || CALLOUT_PALETTE.neutral;
  const surface = resolveCalloutSurface(block.props);
  const isNone = surface === 'none';
  const isNeutral = color === 'neutral';

  const {
    menuPos,
    setMenuPos,
    hexDraft,
    setHexDraft,
    pickColor,
    pickSurface,
    applyHexDraft,
  } = useCalloutMenu({ block, onUpdate });

  const [title, setTitle] = useState(block.content || '');
  const titleRef = useRef(null);
  const titleValueRef = useRef(title);
  useEffect(() => {
    if ((block.content || '') !== titleValueRef.current) {
      titleValueRef.current = block.content || '';
      setTitle(block.content || '');
    }
  }, [block.content]);

  const handleTitleChange = (e) => {
    const value = e.target.value;
    titleValueRef.current = value;
    setTitle(value);
    onUpdate(blockRef.current.id, { content: value });
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const live = blockRef.current;
      const visible = live.props?.showAll
        ? choicePaths(live)
        : choicePaths(live).filter((path) => path.id === activeChoicePathId(live));
      const first = flattenBlocks(visible).find((b) => EDITABLE_TYPES.includes(b.type));
      if (first) focusEditable(first.id, { atStart: true });
      return;
    }
    const el = titleRef.current;
    const id = blockRef.current.id;
    if (e.key === 'ArrowUp' && el && isCaretOnFirstLine(el)) {
      if (onNavigate?.(id, 'up')) e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown' && el && isCaretOnLastLine(el)) {
      if (onNavigate?.(id, 'down')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowLeft' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtStart(el)
    ) {
      if (onNavigate?.(id, 'left')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowRight' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtEnd(el)
    ) {
      if (onNavigate?.(id, 'right')) e.preventDefault();
    }
  };

  const setView = (patch) => {
    const live = blockRef.current;
    onUpdate(live.id, { props: { ...live.props, ...patch } });
  };

  const addPath = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const live = blockRef.current;
    const path = createChoicePath(nextChoiceLabel(choicePaths(live)));
    onUpdate(live.id, {
      props: { ...live.props, activePathId: path.id, showAll: false },
      children: [...(live.children || []), path],
    });
    const first = path.children?.[0];
    if (first) setTimeout(() => focusEditable(first.id, { atStart: true }), 0);
  };

  const removePath = (pathId) => {
    const live = blockRef.current;
    const remaining = choicePaths(live).filter((path) => path.id !== pathId);
    if (remaining.length === 0) return;
    const extras = (live.children || []).filter((child) => child.type !== 'choice-path');
    const nextActive =
      live.props?.activePathId === pathId ? remaining[0].id : live.props?.activePathId;
    onUpdate(live.id, {
      props: { ...live.props, activePathId: nextActive },
      children: [...remaining, ...extras],
    });
  };

  const focusVisibleEditable = () => {
    const live = blockRef.current;
    const visible = live.props?.showAll
      ? choicePaths(live)
      : choicePaths(live).filter((path) => path.id === activeChoicePathId(live));
    const lastEditable = flattenBlocks(visible)
      .reverse()
      .find((b) => EDITABLE_TYPES.includes(b.type));
    if (lastEditable) focusEditable(lastEditable.id, { preventScroll: true });
  };

  const isOwnBackground = (e) => {
    const clickedWrapper = e.target.closest('[data-block-id]');
    return clickedWrapper?.getAttribute('data-block-id') === block.id;
  };

  const handleContextMenu = (e) => {
    if (!isOwnBackground(e)) return;
    if (e.target.closest('.choice-title, .choice-tabs')) return;
    e.preventDefault();
    e.stopPropagation();
    setMenuPos({ x: e.clientX, y: e.clientY });
  };

  const choiceStyle = {
    ...calloutSurfaceStyle({ isQuote: false, isNone, isNeutral, surface, rgb }),
    ...indentStyle(block),
  };

  return (
    <div
      className={`choice-block${showAll ? ' choice-block--all' : ''}${isNone ? ' choice-block--none' : ''}`}
      style={choiceStyle}
      onContextMenu={handleContextMenu}
    >
      <ContentEditable
        innerRef={titleRef}
        html={title}
        onChange={handleTitleChange}
        onKeyDown={handleTitleKeyDown}
        className="choice-title doc-editable"
        tagName="div"
        placeholder="Question…"
      />
      <div className="choice-tabs" role="tablist" aria-label="Choice paths">
        {paths.map((path, index) => (
          <ChoiceTab
            key={path.id}
            path={path}
            color={choiceTabColor(index)}
            selected={!showAll && path.id === activeId}
            canRemove={paths.length > 1}
            onSelect={() => setView({ activePathId: path.id, showAll: false })}
            onRename={(content) => onUpdate(path.id, { content })}
            onRemove={() => removePath(path.id)}
          />
        ))}
        <button
          type="button"
          role="tab"
          aria-selected={showAll}
          className={`choice-tab choice-tab--all${showAll ? ' selected' : ''}`}
          onClick={() => setView({ showAll: true })}
        >
          All
        </button>
        <button
          type="button"
          className="choice-tab-add"
          onClick={addPath}
          title="Add option"
          aria-label="Add option"
        >
          +
        </button>
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className="choice-body" onClick={(e) => {
        if (e.target.closest('[data-block-id]')) return;
        focusVisibleEditable();
      }}>
        {paths.map((path, index) => (
          <ChoicePathBlock
            key={path.id}
            {...passThrough}
            onUpdate={onUpdate}
            onNavigate={onNavigate}
            block={path}
            color={choiceTabColor(index)}
            visible={showAll || path.id === activeId}
            showLabel={showAll}
          />
        ))}
      </div>
      {menuPos && (
        <CalloutMenu
          blockId={block.id}
          menuPos={menuPos}
          onClose={() => setMenuPos(null)}
          surface={surface}
          color={color}
          rgb={rgb}
          isNeutral={isNeutral}
          hexDraft={hexDraft}
          setHexDraft={setHexDraft}
          pickSurface={pickSurface}
          pickColor={pickColor}
          applyHexDraft={applyHexDraft}
          showIcon={false}
          isQuote={false}
        />
      )}
    </div>
  );
};

const ChoiceTab = ({ path, color, selected, canRemove, onSelect, onRename, onRemove }) => {
  const [label, setLabel] = useState(htmlToText(path.content || ''));
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const valueRef = useRef(label);

  useEffect(() => {
    const next = htmlToText(path.content || '');
    if (next !== valueRef.current) {
      valueRef.current = next;
      setLabel(next);
    }
  }, [path.content]);

  useEffect(() => {
    if (!editing) return undefined;
    const input = inputRef.current;
    if (!input) return undefined;
    input.focus();
    input.select();
    const again = window.setTimeout(() => {
      input.focus();
      input.select();
    }, 0);
    return () => window.clearTimeout(again);
  }, [editing]);

  const stopEdit = () => setEditing(false);

  const startEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    setEditing(true);
  };

  const handleChange = (e) => {
    const value = e.target.value.replace(/\n/g, '');
    valueRef.current = value;
    setLabel(value);
    onRename(value);
  };

  return (
    <div
      className={`choice-tab${selected ? ' selected' : ''}${editing ? ' choice-tab--editing' : ''}`}
      style={{ '--choice-tab-color': color }}
    >
      <button
        type="button"
        role="tab"
        aria-selected={selected}
        className="choice-tab-hit"
        onClick={onSelect}
        tabIndex={-1}
      />
      {editing ? (
        <input
          ref={inputRef}
          className="choice-tab-label choice-tab-label--input"
          value={label}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              inputRef.current?.blur();
              stopEdit();
            }
          }}
          onBlur={stopEdit}
          placeholder="Option"
          spellCheck={false}
        />
      ) : (
        <span className="choice-tab-label">{label || 'Option'}</span>
      )}
      <button
        type="button"
        className="choice-tab-edit"
        title="Rename option"
        aria-label="Rename option"
        onMouseDown={(e) => e.preventDefault()}
        onClick={startEdit}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      </button>
      {canRemove && (
        <button
          type="button"
          className="choice-tab-remove"
          title="Remove option"
          aria-label="Remove option"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};
